import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const api = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_APP_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.userToken;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      headers.set("Client-Time", new Date().toISOString());
      headers.set("Client-Time-Offset", new Date().getTimezoneOffset());
      return headers;
    },
  }),
  reducerPath: "adminApi",
  tagTypes: ["User", "Inventory", "InventorySearch", "UserList"],
  endpoints: (build) => ({
    getUser: build.query({
      query: () => ({
        url: "user/",
        method: "GET",
      }),
      providesTags: ["User"],
      invalidatesTags: ["User"],
    }),
    registerUser: build.mutation({
      query: (body) => ({
        url: "user/register",
        method: "POST",
        body,
      }),
      providesTags: ["UserList"],
      invalidatesTags: ["UserList"],
    }),
    login: build.mutation({
      query: (body) => ({
        url: "user/login",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Expense", "Enquiry"],
    }),
    updateUser: build.mutation({
      query: (body) => ({
        url: "user/update",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["UserList"],
      providesTags: ["UserList"],
    }),
    deleteUser: build.mutation({
      query: (body) => ({
        url: "user/delete",
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["UserList"],
      providesTags: ["UserList"],
    }),
    getAllUsers: build.query({
      query: () => ({
        url: "user/all-users",
        method: "GET",
      }),
      providesTags: ["UserList"],
      invalidatesTags: ["UserList"],
    }),
    getAllAssignList: build.query({
      query: () => ({
        url: 'user/assign-to',
        method: 'get'
      })
    }),
    addNewInventory: build.mutation({
      query: (body) => ({
        url: "/inventory",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Inventory"],
    }),
    getInventory: build.query({
      query: ({ page, pageSize, sort, search, filter }) => ({
        url: "inventory/",
        method: "GET",
        params: { page, pageSize, sort, search, filter },
      }),
      providesTags: ["Inventory"],
    }),

    searchInventory: build.query({
      query: ({ query }) => ({
        url: `inventory/search`,
        method: "GET",
        params: { query },
      }),
      providesTags: ["InventorySearch"],
    }),
    addEnquiry: build.mutation({
      query: (body) => ({
        url: "enquiry/createEnquiry",
        method: "POST",
        body,
      }),
      providesTags: ["Enquiry"],
      invalidatesTags: ["Enquiry"],
    }),
    deleteEnquiry: build.mutation({
      query: (body) => ({
        url: "enquiry/deleteEnquiry",
        method: "DELETE",
        body,
      }),
      providesTags: ["Enquiry"],
      invalidatesTags: ["Enquiry"],
    }),
    updateEnquiry: build.mutation({
      query: (body) => ({
        url: "enquiry/updateEnquiry",
        method: "PUT",
        body,
      }),
      providesTags: ["Enquiry"],
      invalidatesTags: ["Enquiry"],
    }),
    updateInventory: build.mutation({
      query: (body) => ({
        url: "inventory",
        method: "PUT",
        body,
      }),
      providesTags: ["Inventory"],
      invalidatesTags: ["Inventory"],
    }),
    updateImages: build.mutation({
      query: (body) => ({
        url: "inventory/images",
        method: "PUT",
        body: body,
      }),
      providesTags: ["Inventory"],
      invalidatesTags: ["Inventory"],
    }),
    closeEnquiry: build.mutation({
      query: (body) => ({
        url: `enquiry/closeEnquiry`,
        method: "PUT",
        body,
      }),
      providesTags: ["Enquiry"],
      invalidatesTags: ["Enquiry"],
    }),
    exportEnquiries: build.query({
      query: () => ({
        url: "enquiry/export",
        method: "GET",
      }),
      providesTags: ["Enquiry"],
      invalidatesTags: ["Enquiry"],
    }),
    getFilteredEnquiries: build.query({
      query: ({ page, pageSize, search, ...filters }) => ({
        url: "enquiry/filtered",
        method: "GET",
        params: { page, pageSize, search, ...filters },
      }),
      providesTags: ["Enquiry"],
      invalidatesTags: ["Enquiry"],
    }),
    getAllEnquiries: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "enquiry/",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Enquiry"],
    }),
    getSpecificEnquiry: build.query({
      query: (id) => `enquiry/getEnquiry/${id}`,
      transformResponse: (response) => {
        return response.docs;
      },
      providesTags: ["Enquiry"],
    }),
    archiveSpecificInventory: build.mutation({
      query: (id) => ({
        url: `inventory/archive`,
        method: "PUT",
        body: { id },
      }),
      invalidatesTags: ["Inventory"],
      providesTags: ["Inventory"],
    }),
    unarchiveSpecificInventory: build.mutation({
      query: (id) => ({
        url: `inventory/unarchive`,
        method: "PUT",
        body: { id },
      }),
      invalidatesTags: ["Inventory"],
      providesTags: ["Inventory"],
    }),
    deleteSpecificInventory: build.mutation({
      query: (id) => ({
        url: `inventory/delete`,
        method: "PUT",
        body: { id },
      }),
      invalidatesTags: ["Inventory"],
      providesTags: ["Inventory"],
    }),
    getSpecificInventory: build.query({
      query: (id) => `inventory/${id}`,
      transformResponse: (response) => {
        let transformedObj = {};
        transformedObj = {
          ...response,
          images: response.media?.images.map((image) => ({
            original: image.url,
            thumbnail: image.url,
            fid: image.fid,
          })),
        };
        return transformedObj;
      },
      providesTags: ["Inventory"],
    }),
    getEnquiryStats: build.query({
      query: () => ({
        url: `enquiry/stats`,
        method: "GET",
      }),
      transformResponse: (response) => {
        return response.result[0];
      },
      providesTags: ["Enquiry"],
      invalidatesTags: ["Enquiry"],
    }),
    getInventoryStats: build.query({
      query: () => `inventory/stats`,
      transformResponse: (response) => {
        return response.result[0];
      },
      providesTags: ["Inventory"],
      invalidatesTags: ["Inventory"],
    }),
    changeCarStatus: build.mutation({
      query: (body) => ({
        url: `inventory/status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Inventory"],
      providesTags: ["Inventory"],
    }),
    enquiryAnalytics: build.query({
      query: () => `enquiry/analytics`,
      transformResponse: (response) => {
        return response.result;
      },
      providesTags: ["Enquiry"],
      invalidatesTags: ["Enquiry"],
    }),
    addExpense: build.mutation({
      query: (body) => ({
        url: "expense/create-expense",
        method: "POST",
        body,
      }),
      providesTags: ["Expense"],
      invalidatesTags: ["Expense"],
    }),
    getAllExpenses: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "expense/",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Expense"],
      invalidatesTags: ["Expense"],
    }),
    getExpenseCardStats: build.query({
      query: () => `expense/stats`,
      providesTags: ["Expense"],
      invalidatesTags: ["Expense"],
    }),
    getExpensePlotData: build.query({
      query: ({ startDate, endDate }) => ({
        url: `expense/plot`,
        method: "GET",
        params: { startDate, endDate },
      }),
      transformResponse: (response) => {
        return response.result;
      },
      providesTags: ["Expense"],
      invalidatesTags: ["Expense"],
    }),
    getInventoryPlotData: build.query({
      query: ({ startDate, endDate }) => ({
        url: `inventory/plot`,
        method: "GET",
        params: { startDate, endDate },
      }),
      transformResponse: (response) => {
        return response.result;
      },
      providesTags: ["Inventory"],
      invalidatesTags: ["Inventory"],
    }),
    getEnquiryPlotData: build.query({
      query: ({ startDate, endDate }) => ({
        url: `enquiry/plot`,
        method: "GET",
        params: { startDate, endDate },
      }),
      transformResponse: (response) => {
        return response.result;
      },
      providesTags: ["Enquiry"],
      invalidatesTags: ["Enquiry"],
    }),
    getCustomerDropdown: build.query({
      query: () => ({
        url: `enquiry/customerDropdown`,
        method: "GET",
      }),
      providesTags: ["Enquiry"],
      invalidatesTags: ["Enquiry"],
      transformResponse: (response) => {
        return response.result;
      },
    }),
    initiateTransaction: build.mutation({
      query: (body) => ({
        url: `transaction/initiate`,
        method: "POST",
        body,
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    getSpecificTransaction: build.query({
      query: (id) => `transaction/${id}`,
      transformResponse: (response) => {
        return response.transaction;
      },
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    saveBookingForm: build.mutation({
      query: (body) => ({
        url: `transaction/saveBookingForm`,
        method: "POST",
        body,
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    saveBalanceReceiptForm: build.mutation({
      query: (body) => ({
        url: `transaction/saveBalanceReceiptForm`,
        method: "POST",
        body,
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    saveDeliveryNote: build.mutation({
      query: (body) => ({
        url: `transaction/saveDeliveryNote`,
        method: "POST",
        body,
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    saveInvoice: build.mutation({
      query: (body) => ({
        url: `transaction/saveInvoice`,
        method: "POST",
        body,
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    getAllTransactions: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "transaction/",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    getAllInvoiceTransactions: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "transaction/invoices",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    cancelTransaction: build.mutation({
      query: (id) => ({
        url: `transaction/${id}`,
        method: "DELETE",
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    getSalesStats: build.query({
      query: () => ({
        url: `transaction/stats`,
        method: "GET",
      }),
      transformResponse: (response) => {
        return response.result[0];
      },
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    getSalesPlotData: build.query({
      query: ({ startDate, endDate }) => ({
        url: `transaction/plot`,
        method: "GET",
        params: { startDate, endDate },
      }),
      transformResponse: (response) => {
        return response.result;
      },
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    saveRto: build.mutation({
      query: (body) => ({
        url: `transaction/saveRto`,
        method: "POST",
        body,
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    completeRto: build.mutation({
      query: (body) => ({
        url: `transaction/completeRto`,
        method: "POST",
        body,
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    saveInsurance: build.mutation({
      query: (body) => ({
        url: `transaction/saveInsurance`,
        method: "POST",
        body,
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    completeInsurance: build.mutation({
      query: (body) => ({
        url: `transaction/completeInsurance`,
        method: "POST",
        body,
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    getAllRTOTransactions: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "transaction/rtolist",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
    getAllInsuranceTransactions: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "transaction/insurancelist",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Transaction"],
      invalidatesTags: ["Transaction"],
    }),
  }),
});

export const {
  useGetUserQuery,
  useRegisterUserMutation,
  useLoginMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetAllUsersQuery,
  useGetAllAssignListQuery,
  useGetInventoryQuery,
  useSearchInventoryQuery,
  useAddEnquiryMutation,
  useGetAllEnquiriesQuery,
  useGetFilteredEnquiriesQuery,
  useExportEnquiriesQuery,
  useGetSpecificEnquiryQuery,
  useUpdateEnquiryMutation,
  useUpdateInventoryMutation,
  useCloseEnquiryMutation,
  useUpdateImagesMutation,
  useGetSpecificInventoryQuery,
  useArchiveSpecificInventoryMutation,
  useUnarchiveSpecificInventoryMutation,
  useDeleteSpecificInventoryMutation,
  useGetEnquiryStatsQuery,
  useGetInventoryStatsQuery,
  useAddNewInventoryMutation,
  useChangeCarStatusMutation,
  useEnquiryAnalyticsQuery,
  useAddExpenseMutation,
  useGetAllExpensesQuery,
  useGetExpenseCardStatsQuery,
  useGetExpensePlotDataQuery,
  useGetInventoryPlotDataQuery,
  useGetEnquiryPlotDataQuery,
  useDeleteEnquiryMutation,
  useGetCustomerDropdownQuery,
  useInitiateTransactionMutation,
  useGetSpecificTransactionQuery,
  useSaveBookingFormMutation,
  useSaveBalanceReceiptFormMutation,
  useSaveDeliveryNoteMutation,
  useSaveInvoiceMutation,
  useGetAllTransactionsQuery,
  useGetAllInvoiceTransactionsQuery,
  useCancelTransactionMutation,
  useGetSalesStatsQuery,
  useGetSalesPlotDataQuery,
  useSaveRtoMutation,
  useCompleteRtoMutation,
  useSaveInsuranceMutation,
  useCompleteInsuranceMutation,
  useGetAllRTOTransactionsQuery,
  useGetAllInsuranceTransactionsQuery,
} = api;
